import api, { ApiResponse } from "../api";
import {
  ActivityLogEntry,
  AnalyticsOverview,
  AnalyticsOverviewQuery,
  CreateBatchRequest,
  CreateBatchResponse,
  DenimUploadBatch,
  DenimUploadBatchDetail,
  DenimUploadBatchItem,
  FactoredInvoiceBase,
  FactoredInvoiceDetail,
  FactoredInvoiceListItem,
  FactoringStats,
  ListBatchesQuery,
  ListBatchItemsQuery,
  ListInvoicesQuery,
  NotificationRecipient,
  NotificationRecipientRequest,
  NotificationSettings,
  PagedResponse,
} from "./models";

const BASE_PATH = "/invoicing/factoring";

export type BatchFileKind = "source" | "report";

function getErrorMessage(response: ApiResponse<unknown>): string {
  const data = response?.data as any;
  const message = data?.message;
  if (Array.isArray(message) && message.length) return message.join(". ");
  if (typeof message === "string" && message) return message;
  const err = response?.getError?.();
  if (typeof err === "string") return err;
  return "Request failed";
}

function toParams(query: Record<string, any>): Record<string, string> {
  const params: Record<string, string> = {};
  Object.keys(query).forEach((key) => {
    const value = query[key];
    if (value !== undefined && value !== null && value !== "") params[key] = String(value);
  });
  return params;
}

function getFileNameFromDisposition(disposition: string | undefined, fallback: string): string {
  const match = /filename="?([^";]+)"?/i.exec(disposition || "");
  return match ? match[1] : fallback;
}

class InvoiceFactoringService {
  private static instance: InvoiceFactoringService;

  private constructor() {}

  static getInstance(): InvoiceFactoringService {
    if (!InvoiceFactoringService.instance) {
      InvoiceFactoringService.instance = new InvoiceFactoringService();
    }
    return InvoiceFactoringService.instance;
  }

  public getStats = async (): Promise<FactoringStats> => {
    const response = await api.get<FactoringStats, ApiResponse<FactoringStats>>(`${BASE_PATH}/batches/stats`);
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public getAnalyticsOverview = async (query: AnalyticsOverviewQuery): Promise<AnalyticsOverview> => {
    const response = await api.get<AnalyticsOverview, ApiResponse<AnalyticsOverview>>(`${BASE_PATH}/analytics/overview`, {
      params: toParams(query),
    });
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public getBatches = async (query: ListBatchesQuery): Promise<PagedResponse<DenimUploadBatch>> => {
    const response = await api.get<PagedResponse<DenimUploadBatch>, ApiResponse<PagedResponse<DenimUploadBatch>>>(`${BASE_PATH}/batches`, { params: toParams(query) });
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public getBatch = async (batchId: string): Promise<DenimUploadBatchDetail> => {
    const response = await api.get<DenimUploadBatchDetail, ApiResponse<DenimUploadBatchDetail>>(`${BASE_PATH}/batches/${batchId}`);
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public getBatchItems = async (batchId: string, query: ListBatchItemsQuery): Promise<PagedResponse<DenimUploadBatchItem>> => {
    const response = await api.get<PagedResponse<DenimUploadBatchItem>, ApiResponse<PagedResponse<DenimUploadBatchItem>>>(`${BASE_PATH}/batches/${batchId}/items`, {
      params: toParams(query),
    });
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public getBatchActivity = async (batchId: string): Promise<ActivityLogEntry[]> => {
    const response = await api.get<ActivityLogEntry[], ApiResponse<ActivityLogEntry[]>>(`${BASE_PATH}/batches/${batchId}/activity`);
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public createBatch = async (request: CreateBatchRequest): Promise<CreateBatchResponse> => {
    const formData = new FormData();
    formData.append("file", request.file);
    if (request.name) formData.append("name", request.name);
    formData.append("start", String(request.start));
    const response = await api.post<CreateBatchResponse, ApiResponse<CreateBatchResponse>>(`${BASE_PATH}/batches`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public startBatch = (batchId: string) => this.batchAction(batchId, "start");

  public stopBatch = (batchId: string) => this.batchAction(batchId, "stop");

  public resumeBatch = (batchId: string) => this.batchAction(batchId, "resume");

  public retryFailedItems = (batchId: string) => this.batchAction(batchId, "retry-failed");

  private batchAction = async (batchId: string, action: string): Promise<DenimUploadBatch> => {
    const response = await api.post<DenimUploadBatch, ApiResponse<DenimUploadBatch>>(`${BASE_PATH}/batches/${batchId}/${action}`);
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public downloadBatchFile = async (batchId: string, kind: BatchFileKind): Promise<{ blob: Blob; fileName: string }> => {
    const path = kind === "source" ? "source.csv" : "report.csv";
    const response = await api.get<Blob, ApiResponse<Blob>>(`${BASE_PATH}/batches/${batchId}/${path}`, { responseType: "blob" });
    if (!response.ok()) throw new Error("Unable to download the file");
    return {
      blob: response.data,
      fileName: getFileNameFromDisposition(response.headers?.["content-disposition"], `batch_${kind}.csv`),
    };
  };

  public getInvoices = async (query: ListInvoicesQuery): Promise<PagedResponse<FactoredInvoiceListItem>> => {
    const response = await api.get<PagedResponse<FactoredInvoiceListItem>, ApiResponse<PagedResponse<FactoredInvoiceListItem>>>(`${BASE_PATH}/invoices`, { params: toParams(query) });
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public getInvoice = async (probillNumber: string): Promise<FactoredInvoiceDetail> => {
    const response = await api.get<FactoredInvoiceDetail, ApiResponse<FactoredInvoiceDetail>>(`${BASE_PATH}/invoices/${encodeURIComponent(probillNumber)}`);
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public refreshInvoiceStatus = async (probillNumber: string): Promise<FactoredInvoiceBase> => {
    const response = await api.post<FactoredInvoiceBase, ApiResponse<FactoredInvoiceBase>>(
      `${BASE_PATH}/invoices/${encodeURIComponent(probillNumber)}/refresh-status`
    );
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public getNotificationSettings = async (): Promise<NotificationSettings> => {
    const response = await api.get<NotificationSettings, ApiResponse<NotificationSettings>>(`${BASE_PATH}/notification-recipients`);
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public createRecipient = async (request: NotificationRecipientRequest): Promise<NotificationRecipient> => {
    const response = await api.post<NotificationRecipient, ApiResponse<NotificationRecipient>>(`${BASE_PATH}/notification-recipients`, request);
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public updateRecipient = async (id: string, request: Partial<NotificationRecipientRequest>): Promise<NotificationRecipient> => {
    const response = await api.patch<NotificationRecipient, ApiResponse<NotificationRecipient>>(`${BASE_PATH}/notification-recipients/${id}`, request);
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public deleteRecipient = async (id: string): Promise<void> => {
    const response = await api.delete<void, ApiResponse<void>>(`${BASE_PATH}/notification-recipients/${id}`);
    if (!response.ok()) throw new Error(getErrorMessage(response));
  };
}

export default InvoiceFactoringService;
